import { useCallback, useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router";
import {
  APP_SETTING_LABELS,
  AUDIT_ACTIONS,
  AUDIT_ENTITY_LABELS,
  BORDER_PASS_RULE_LABELS,
  DELIVERY_NOTIFY_SCOPE_LABELS,
  JOURNAL_PAGE_SIZE,
  journalTypeLabel,
  LOT_EVENT_LABELS,
  LOT_EVENT_TYPES,
  type AdminUserRow,
  type JournalEntry,
  type JournalPage,
  type JournalQuery,
} from "@omts/shared";
import { adminApi } from "../../api";
import { fmtDateTime } from "../../time";
import { errMsg } from "./common";

const FIELD_LABELS: Record<string, string> = {
  name: "название",
  bin: "БИН",
  isActive: "активна",
  contact: "менеджер",
  fullName: "ФИО",
  phone: "телефон",
  role: "роль",
  email: "e-mail",
  fields: "поля",
  recipients: "получателей",
  reason: "причина",
  text: "текст",
  deadlineAt: "дедлайн",
  dueAt: "срок оплаты",
  deliveredAt: "доставлено",
  amount: "сумма",
  from: "было",
  to: "стало",
  key: "ключ",
  target: "кому",
  companyId: "компания",
  contactId: "контакт",
  userId: "пользователь",
};

const fmtValue = (v: unknown): string => {
  if (v === null || v === undefined) return "—";
  if (typeof v === "boolean") return v ? "да" : "нет";
  if (typeof v === "string") {
    if (/^\d{4}-\d{2}-\d{2}T/.test(v)) return fmtDateTime(v);
    return v;
  }
  if (typeof v === "number") return v.toLocaleString("ru-RU");
  if (Array.isArray(v)) return v.map(fmtValue).join(", ");
  if (typeof v === "object") return Object.entries(v as Record<string, unknown>).map(([k, x]) => `${FIELD_LABELS[k] ?? k}: ${fmtValue(x)}`).join("; ");
  return String(v);
};

/** Значение настройки по-русски, где есть словарь подписей. */
function settingValue(key: string, v: unknown): string {
  if (key === "border_pass_rule" && typeof v === "string") return (BORDER_PASS_RULE_LABELS as Record<string, string>)[v] ?? v;
  if (key === "delivery_notify_scope" && typeof v === "string") return (DELIVERY_NOTIFY_SCOPE_LABELS as Record<string, string>)[v] ?? v;
  return fmtValue(v);
}

/** Что показать в колонке «Объект» и «Подробности». */
function describe(e: JournalEntry): { object: string; details: string } {
  const p = e.payload;
  if (e.source === "lot") {
    const details = Object.entries(p)
      .map(([k, v]) => `${FIELD_LABELS[k] ?? k}: ${fmtValue(v)}`)
      .join("; ");
    return { object: e.lotNumber ?? "заявка", details };
  }
  if (e.type === "settings.update") {
    const key = String(p.key ?? e.entityId ?? "");
    const label = (APP_SETTING_LABELS as Record<string, string>)[key] ?? key;
    return { object: label, details: `${settingValue(key, p.from)} → ${settingValue(key, p.to)}` };
  }
  const entityLabel = (AUDIT_ENTITY_LABELS as Record<string, string>)[e.entityType ?? ""] ?? e.entityType ?? "";
  const name = typeof p.name === "string" ? p.name : typeof p.email === "string" ? p.email : "";
  const rest = Object.entries(p).filter(([k]) => k !== "name");
  const details = rest.map(([k, v]) => `${FIELD_LABELS[k] ?? k}: ${fmtValue(v)}`).join("; ");
  return { object: name ? `${entityLabel}: ${name}` : entityLabel, details };
}

type Filters = { from: string; to: string; userId: string; type: string; lot: string; page: number };

const readFilters = (sp: URLSearchParams): Filters => ({
  from: sp.get("from") ?? "",
  to: sp.get("to") ?? "",
  userId: sp.get("userId") ?? "",
  type: sp.get("type") ?? "",
  lot: sp.get("lot") ?? "",
  page: Math.max(1, Number(sp.get("page")) || 1),
});

/** Дата «с» → начало дня, «по» → конец дня в локальном поясе браузера, ISO для API. */
const dayStart = (d: string) => (d ? new Date(`${d}T00:00:00`).toISOString() : undefined);
const dayEnd = (d: string) => (d ? new Date(`${d}T23:59:59.999`).toISOString() : undefined);

/** Вкладка «Журнал» (16.6): объединённая лента событий лотов и действий над справочниками/пользователями/настройками. */
export function AuditTab() {
  const [sp, setSp] = useSearchParams();
  const filters = readFilters(sp);
  const [data, setData] = useState<JournalPage | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [users, setUsers] = useState<AdminUserRow[]>([]);

  useEffect(() => {
    adminApi.users().then(setUsers).catch(() => setUsers([]));
  }, []);

  const key = sp.toString();
  const load = useCallback(() => {
    const f = readFilters(new URLSearchParams(key));
    const q: JournalQuery = {
      from: dayStart(f.from),
      to: dayEnd(f.to),
      userId: f.userId || undefined,
      type: f.type || undefined,
      lot: f.lot.trim() || undefined,
      page: f.page,
    };
    adminApi
      .audit(q)
      .then((d) => {
        setData(d);
        setError(null);
      })
      .catch((e: unknown) => setError(errMsg(e)));
  }, [key]);
  useEffect(load, [load]);

  const update = (patch: Partial<Filters>) => {
    const next = { ...filters, ...patch, page: patch.page ?? 1 };
    const params = new URLSearchParams();
    for (const [k, v] of Object.entries(next)) {
      if (k === "page") {
        if (v !== 1) params.set(k, String(v));
      } else if (v) params.set(k, String(v));
    }
    setSp(params, { replace: true });
  };

  const pages = data ? Math.max(1, Math.ceil(data.total / JOURNAL_PAGE_SIZE)) : 1;
  const hasFilters = Boolean(filters.from || filters.to || filters.userId || filters.type || filters.lot);

  return (
    <>
      <div className="toolbar journal-filters">
        <div className="row">
          <label className="row hint">
            с <input type="date" value={filters.from} onChange={(e) => update({ from: e.target.value })} />
          </label>
          <label className="row hint">
            по <input type="date" value={filters.to} onChange={(e) => update({ to: e.target.value })} />
          </label>
          <select value={filters.userId} onChange={(e) => update({ userId: e.target.value })}>
            <option value="">Все пользователи</option>
            {users.map((u) => (
              <option key={u.id} value={u.id}>
                {u.fullName}
              </option>
            ))}
          </select>
          <select value={filters.type} onChange={(e) => update({ type: e.target.value })}>
            <option value="">Все типы</option>
            <optgroup label="События по заявкам">
              {LOT_EVENT_TYPES.map((t) => (
                <option key={t} value={t}>
                  {LOT_EVENT_LABELS[t]}
                </option>
              ))}
            </optgroup>
            <optgroup label="Справочники, пользователи, настройки">
              {AUDIT_ACTIONS.map((a) => (
                <option key={a} value={a}>
                  {journalTypeLabel(a)}
                </option>
              ))}
            </optgroup>
          </select>
          <input placeholder="Номер заявки" value={filters.lot} onChange={(e) => update({ lot: e.target.value })} style={{ minWidth: 140 }} />
          {hasFilters && (
            <button type="button" className="btn small" onClick={() => setSp(new URLSearchParams(), { replace: true })}>
              Сбросить
            </button>
          )}
        </div>
        <span className="muted">{data ? `найдено ${data.total}` : ""}</span>
      </div>
      {error && <p className="error">{error}</p>}
      <section className="card">
        {!data ? (
          <p className="muted">Загрузка…</p>
        ) : data.items.length === 0 ? (
          <p className="muted">{hasFilters ? "По этим условиям записей нет." : "Журнал пуст."}</p>
        ) : (
          <table className="table journal">
            <thead>
              <tr>
                <th>Когда</th>
                <th>Кто</th>
                <th>Действие</th>
                <th>Объект</th>
                <th>Подробности</th>
              </tr>
            </thead>
            <tbody>
              {data.items.map((e) => {
                const { object, details } = describe(e);
                return (
                  <tr key={`${e.source}:${e.id}`}>
                    <td className="nowrap">{fmtDateTime(e.createdAt)}</td>
                    <td>{e.actorName ?? (e.actorCompanyName ? `${e.actorCompanyName} (перевозчик)` : <span className="muted">система</span>)}</td>
                    <td>{journalTypeLabel(e.type)}</td>
                    <td>
                      {e.lotId ? (
                        <Link to={`/lots/${e.lotId}`} className="lot-link">
                          <b>{e.lotNumber ?? "заявка"}</b>
                        </Link>
                      ) : (
                        object
                      )}
                    </td>
                    <td className="journal-details">{details || <span className="muted">—</span>}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </section>
      {data && pages > 1 && (
        <div className="row" style={{ justifyContent: "center", marginTop: 12 }}>
          <button type="button" className="btn small" disabled={filters.page <= 1} onClick={() => update({ page: filters.page - 1 })}>
            ← Новее
          </button>
          <span className="muted">
            страница {filters.page} из {pages}
          </span>
          <button type="button" className="btn small" disabled={filters.page >= pages} onClick={() => update({ page: filters.page + 1 })}>
            Старее →
          </button>
        </div>
      )}
      <p className="hint">События по заявкам пишут сами действия (публикация, ставки, доставка); действия над справочниками, пользователями, шаблонами и настройками — таблица audit_log.</p>
    </>
  );
}
